/* eslint-disable @typescript-eslint/no-explicit-any */

import type { JSX, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';

import { ErrorFieldCheck } from '../ErrorValidation';
import InputProvider from '../InputProvider';

interface AutocompleteProps<T = any> {
  onSelect?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  required?: boolean;
  footer?: ReactNode;
  inputValue?: string;
  name: string;
  placeholder?: string;
  suggestions?: T[];
  formatSuggestion?: (item: T) => string;
  formik?: any;
  dispatch?: any;
  store?: any;
  apiService?: any;

  'data-testid'?: string;
}

const Autocomplete = <T,>({
  onSelect,
  onBlur,
  onInputChange,
  label,
  required,
  footer,
  inputValue,
  name,
  placeholder,
  suggestions,
  formatSuggestion = (i: T) => String(i),
  formik,
  dispatch,
  store,
  apiService,
  'data-testid': dataTestId
}: AutocompleteProps<T>): JSX.Element => {
  const [isTouch, setIsTouch] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasSuggestions = Boolean(
    suggestions && suggestions.length
  );

  useEffect(() => {
    setIsOpen(hasSuggestions);
  }, [hasSuggestions]);

  const handleSelect = (value: string) => {
    onSelect?.(value);
    setIsOpen(false);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    onBlur?.(e);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onInputChange?.(e);
    if (hasSuggestions) setIsOpen(true);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  return (
    <InputProvider
      formik={formik}
      name={name}
      dispatch={dispatch}
      store={store}
      apiService={apiService}
    >
      {({ hasError, errorMessage, isTouched }) => (
        <Box
          data-testid={dataTestId}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%'
          }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ width: '100%' }}>
              <TextField
                variant="outlined"
                fullWidth
                inputRef={inputRef}
                name={name}
                value={inputValue}
                placeholder={placeholder}
                label={label || ''}
                required={!!required}
                slotProps={{
                  input: { 'aria-required': !!required }
                }}
                error={hasError && isTouched}
                onBlur={handleBlur}
                onChange={handleInputChange}
                onFocus={() =>
                  hasSuggestions && setIsOpen(true)
                }
              />

              <ErrorFieldCheck
                hasError={hasError}
                isTouch={isTouch}
                errorMessage={errorMessage as React.ReactNode}
                setIsTouch={setIsTouch}
              />

              {footer && (
                <Box
                  sx={{
                    mt: '3px',
                    fontSize: '12px',
                    fontWeight: 300,
                    '& a': {
                      color: 'var(--cargill-green)'
                    }
                  }}
                >
                  {footer}
                </Box>
              )}

              {hasSuggestions && (
                <Popper
                  open={isOpen}
                  anchorEl={inputRef.current}
                  placement="bottom-start"
                  modifiers={[
                    {
                      name: 'offset',
                      options: { offset: [0, 4] }
                    }
                  ]}
                  sx={{
                    width: inputRef.current
                      ? inputRef.current.clientWidth
                      : undefined,
                    zIndex: 1300
                  }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      maxHeight: 240,
                      overflow: 'auto'
                    }}
                  >
                    <List dense disablePadding>
                      {suggestions!.map(
                        (suggestion, i) => {
                          const value =
                            formatSuggestion(suggestion);

                          return (
                            <ListItemButton
                              key={`${value}${i}`}
                              onClick={() =>
                                handleSelect(value)
                              }
                              sx={{ px: 2, py: 1 }}
                            >
                              {value}
                            </ListItemButton>
                          );
                        }
                      )}
                    </List>
                  </Paper>
                </Popper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      )}
    </InputProvider>
  );
};

export default Autocomplete;